"use client";
import React, { useState } from "react";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { EventTicketTier } from "@prisma/client";
import { Button } from "~/components/v3/themed/Button";
import { FaCheck } from "react-icons/fa";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardFooter,
} from "~/components/v3/shadcn/Card";
import { If } from "~/components/v3/Conditional";
import { GoEyeClosed } from "react-icons/go";
import { Input } from "~/components/v3/themed/Input";
import { LuCopy } from "react-icons/lu";
import { toast } from "react-toastify";
import useUserPemissions from "~/hooks/useUserPermissions";
import { cn } from "~/utils/fontsConfig";

type Props = {
    event: EventBySlugOutput;
    setSelectedTier: (selectedTier: EventTicketTier | null) => void;
    setDiscountCode: (discountCode: string) => void;
    onSelection: () => void;
};

const PurchaseTicketTierCard = ({
    event,
    setSelectedTier,
    setDiscountCode,
    onSelection,
}: Props) => {
    const [selectedTierId, setSelectedTierId] = useState<string | null>(null);

    const { isAdmin, isManager } = useUserPemissions({
        event: event || null,
    });

    if (
        !event?.eventTicketPackage ||
        event?.eventTicketPackage?.eventTicketTiers?.length === 0
    ) {
        return null;
    }

    const hasDiscountCodes = !!event.eventTicketPackage.eventTicketTiers.find(
        (tt) => !!tt.discountCodes.length,
    );

    const handleSelection = (tier: EventTicketTier) => {
        setSelectedTierId(tier.id);
        setSelectedTier(tier);
    };

    const handlePurchase = () => {
        if (!selectedTierId) {
            return;
        }
        onSelection();
    };

    const isAvailable = (tier: EventTicketTier) => {
        if (!tier.usageLimit) return true;
        if (tier.usageLimit && tier.usedTimes < tier.usageLimit) return true;
        return false;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Get Tickets</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="mb-4">
                    Welcome! Please choose your desired ticket:
                </p>
                <div className="space-y-2">
                    {event.eventTicketPackage.eventTicketTiers.map((tier) => (
                        <div
                            key={tier.id}
                            className={cn(
                                `
                                flex cursor-pointer
                                items-center justify-between rounded-lg
                                p-3`,
                                !isAvailable(tier) && "opacity-50",
                            )}
                            style={{
                                backgroundColor:
                                    selectedTierId === tier.id
                                        ? "#373A41"
                                        : "#1B1E23",
                            }}
                            onClick={() =>
                                isAvailable(tier) && handleSelection(tier)
                            }
                        >
                            <div className="flex items-center space-x-3">
                                <div className="flex h-4 w-4 items-center justify-center">
                                    {selectedTierId === tier.id ? (
                                        <FaCheck className="text-green-500" />
                                    ) : (
                                        <span className="h-4 w-4" />
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-semibold">
                                            {tier.name}
                                        </p>
                                        <If condition={!tier.isPublic}>
                                            <GoEyeClosed className="text-v3-primary" />
                                            <If
                                                condition={isAdmin || isManager}
                                            >
                                                <LuCopy
                                                    onClick={() => {
                                                        navigator.clipboard
                                                            .writeText(
                                                                `https://app.getriver.io/events/${event.slug}/ticket/${tier.id}`,
                                                            )
                                                            .then(() =>
                                                                toast.success(
                                                                    "Copied secret URL to clipboard",
                                                                ),
                                                            )
                                                            .catch(() =>
                                                                toast.error(
                                                                    "Failed to copy secret URL",
                                                                ),
                                                            );
                                                    }}
                                                />
                                            </If>
                                        </If>
                                    </div>
                                    {tier.description && (
                                        <p className="text-sm text-gray-400">
                                            {tier.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <span className="flex flex-col items-end font-bold">
                                {tier.isFree
                                    ? "Free"
                                    : tier.priceDollars !== null &&
                                        tier.priceDollars !== undefined
                                      ? `$${tier.priceDollars}${
                                            tier.priceCents !== null &&
                                            tier.priceCents !== undefined
                                                ? `.${tier.priceCents.toString().padStart(2, "0")}`
                                                : ".00"
                                        }`
                                      : "Price not available"}
                                {(tier.usageLimit || tier.usedTimes > 0) && (
                                    <p className="text-sm font-light text-gray-400">
                                        {tier.usedTimes ?? 0} /{" "}
                                        {tier.usageLimit ?? "∞"}
                                    </p>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
                <If condition={hasDiscountCodes}>
                    <div className="mt-4 flex items-center gap-2 text-gray-400">
                        <div className="grow"></div>
                        Discount code:
                        <Input
                            placeholder="DISCOUNT-ABC"
                            className="w-36 bg-transparent"
                            onChange={(e) => setDiscountCode(e.target.value)}
                        />
                    </div>
                </If>
            </CardContent>
            <CardFooter>
                <Button
                    onClick={handlePurchase}
                    disabled={!selectedTierId}
                    className="w-full"
                >
                    Get Ticket
                </Button>
            </CardFooter>
        </Card>
    );
};

export default PurchaseTicketTierCard;
